import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import RecomendadoCard from './RecomendadoCard';
import SectionTitle from './SectionTitle';

// Barajado aleatorio (Fisher-Yates) para rotar los productos en cada carga
const barajarArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const RecomendadosList = ({ addToCart, onVerTodo, vistaAfiliado = false }) => {
  const [productos, setProductos]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const scrollRef                           = useRef(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hayOverflow, setHayOverflow]       = useState(false);
  const isPausedRef                         = useRef(false);

  // ── Fetch ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchRecomendados = async () => {
      try {
        const q = query(
          collection(db, 'catalogo'),
          where('recomendado', '==', true),
          where('visible', '==', true)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        let docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Si no hubiera productos con recomendado: true, usar los visibles como respaldo
        if (docs.length === 0) {
          const qFallback = query(
            collection(db, 'catalogo'),
            where('visible', '!=', false)
          );
          const snapFallback = await getDocs(qFallback);
          docs = snapFallback.docs.map(d => ({ id: d.id, ...d.data() }));
        }

        // Aleatorizar el orden cada vez que se carga la pantalla para rotar productos
        docs = barajarArray(docs);
        setProductos(docs);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRecomendados();
    return () => { cancelled = true; };
  }, []);

  // ── Scroll arrows & overflow ──────────────────────────────────────────
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tieneOverflow = el.scrollWidth > el.clientWidth + 4;
    setHayOverflow(tieneOverflow);
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(tieneOverflow && (el.scrollLeft + el.clientWidth < el.scrollWidth - 6));
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkScroll) : null;
    if (ro) ro.observe(el);

    const timer = setTimeout(checkScroll, 150);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      if (ro) ro.disconnect();
      clearTimeout(timer);
    };
  }, [checkScroll, productos]);

  // ── Autoplay suave y desfasado (cada 5.5s) con pausa en interacción ───
  useEffect(() => {
    if (!hayOverflow || productos.length <= 1) return;

    const interval = setInterval(() => {
      if (isPausedRef.current) return;
      const el = scrollRef.current;
      if (!el) return;

      const card = el.querySelector('.recomendado-item-wrap');
      const step = card ? card.offsetWidth + 12 : 180;

      // Si llegó al final, vuelve suavemente al inicio
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 15) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 5500);

    return () => clearInterval(interval);
  }, [hayOverflow, productos]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 270, behavior: 'smooth' });
  };

  // ── States ───────────────────────────────────────────────────────────
  if (loading)           return <SkeletonRow />;
  if (error)             return null;
  if (!productos.length) return null;

  return (
    <section style={{
      width: '100%',
      fontFamily: 'var(--font-body)',
    }}>

      {/* Título estandarizado con acción "Ver todo" */}
      <SectionTitle
        accion={onVerTodo && (hayOverflow || productos.length > 4) ? 'Ver todo' : undefined}
        onAccion={onVerTodo}
      >
        Recomendados para ti
      </SectionTitle>

      {/* Carrusel */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => { isPausedRef.current = true; }}
        onMouseLeave={() => { isPausedRef.current = false; }}
        onTouchStart={() => { isPausedRef.current = true; }}
        onTouchEnd={() => { setTimeout(() => { isPausedRef.current = false; }, 2000); }}
      >

        {canScrollLeft && <ArrowBtn dir="left" onClick={() => scroll(-1)} />}

        <div
          ref={scrollRef}
          className="scroll-oculto"
          style={{
            display: 'flex',
            justifyContent: hayOverflow ? 'flex-start' : 'center',
            gap: 'var(--space-3)',
            overflowX: hayOverflow ? 'auto' : 'visible',
            padding: 'var(--space-2) var(--space-4) var(--space-2)',
            scrollSnapType: hayOverflow ? 'x mandatory' : 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {productos.map((producto, i) => (
            <div
              key={producto.id}
              className="recomendado-item-wrap"
              style={{
                flex: '0 0 auto',
                width: 'clamp(135px, 36vw, 172px)',
                scrollSnapAlign: 'start',
                display: 'flex',
              }}
            >
              <RecomendadoCard
                producto={producto}
                addToCart={addToCart}
                index={i}
                vistaAfiliado={vistaAfiliado}
              />
            </div>
          ))}
        </div>

        {canScrollRight && <ArrowBtn dir="right" onClick={() => scroll(1)} />}
      </div>

      <style>{`
        .scroll-oculto::-webkit-scrollbar { display: none; }
        .scroll-oculto { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

// ── Flecha de navegación — estilo del sistema ─────────────────────────
const ArrowBtn = ({ dir, onClick }) => {
  const isLeft = dir === 'left';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isLeft ? 'Anterior' : 'Siguiente'}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [isLeft ? 'left' : 'right']: '4px',
        zIndex: 10,
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        border: '0.5px solid var(--border-default)',
        backgroundColor: 'var(--color-blanco)',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d={isLeft ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'}
          stroke="var(--color-verde)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

// ── Skeleton loader — tokens del sistema ──────────────────────────────
const SkeletonRow = () => (
  <div style={{ padding: 'var(--space-5) var(--space-4)' }}>
    <div style={{
      width: '220px',
      height: '26px',
      borderRadius: 'var(--radius-md)',
      background: 'linear-gradient(90deg, #f0f0ee 25%, #fff 50%, #f0f0ee 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeleton-loading 1.2s ease-in-out infinite',
      margin: '0 auto var(--space-4)',
    }} />
    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{
          flex: '0 0 auto',
          width: '175px',
          height: '280px',
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(90deg, #f0f0ee 25%, #fff 50%, #f0f0ee 75%)',
          backgroundSize: '200% 100%',
          animation: 'skeleton-loading 1.2s ease-in-out infinite',
        }} />
      ))}
    </div>
    <style>{`
      @keyframes skeleton-loading {
        0%   { background-position:  200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

export default RecomendadosList;
